import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle, LockKey, UsersThree, WarningCircle } from "@phosphor-icons/react";

export default function TeamInviteAccept() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Davet bağlantısı eksik.");
      setLoading(false);
      return;
    }
    api.get(`/team/invitations/accept?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setInvite(r.data);
        setName(r.data?.name || "");
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Davet bağlantısı doğrulanamadı."))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (name.trim().length < 2) return setError("Ad soyad en az 2 karakter olmalıdır.");
    if (password.length < 10 || !/[A-Za-zÇĞİÖŞÜçğıöşü]/.test(password) || !/\d/.test(password)) {
      return setError("Şifre en az 10 karakter, en az bir harf ve bir rakam içermelidir.");
    }
    if (password !== confirm) return setError("Şifreler eşleşmiyor.");
    setSaving(true);
    try {
      const r = await api.post("/team/invitations/accept", { token, name: name.trim(), password });
      sessionStorage.setItem("h3poe_onboarding_complete", "1");
      window.location.href = r.data?.redirect_url || "/app";
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || "Davet tamamlanamadı.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="team-invite-accept-page">
      <div className="w-full max-w-xl bg-white border border-slate-200 shadow-xl">
        <div className="bg-[#0F172A] text-white p-6">
          <img src="/brand/palletnext-logo.png" alt="PalletNext" className="h-14 w-auto object-contain" />
          <div className="mt-5 flex items-center gap-2 text-blue-300 text-xs uppercase tracking-[0.2em] font-bold">
            <UsersThree size={18} weight="duotone" /> Ekip Daveti
          </div>
          <h1 className="mt-2 text-3xl font-extrabold">PalletNext ekibine katıl</h1>
        </div>

        <div className="p-6 sm:p-8">
          {loading && <div className="text-slate-500">Davet doğrulanıyor…</div>}

          {!loading && error && !invite && (
            <div className="border border-red-200 bg-red-50 text-red-700 p-4 flex gap-3">
              <WarningCircle size={22} className="shrink-0" />
              <div>{error}</div>
            </div>
          )}

          {!loading && invite && (
            <>
              <div className="border border-blue-100 bg-blue-50 p-4 mb-6">
                <div className="flex gap-3">
                  <CheckCircle size={22} className="text-blue-600 shrink-0" weight="fill" />
                  <div className="text-sm">
                    <b>{invite.owner_name}</b> sizi <b>{invite.role_label}</b> rolüyle ekibine davet etti.<br />
                    <span className="text-slate-600">Davet edilen e-posta: {invite.email}</span>
                  </div>
                </div>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">E-posta</span>
                  <input value={invite.email || ""} disabled className="mt-1 w-full border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-500" />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Ad Soyad</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus className="mt-1 w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#2563EB]" />
                </label>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Şifre</span>
                    <div className="relative mt-1">
                      <LockKey size={17} className="absolute left-3 top-3 text-slate-400" />
                      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full border border-slate-300 pl-10 pr-3 py-2.5 outline-none focus:border-[#2563EB]" />
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Şifre Tekrar</span>
                    <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1 w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#2563EB]" />
                  </label>
                </div>
                <div className="text-xs text-slate-500">En az 10 karakter, en az bir harf ve bir rakam.</div>

                {error && <div className="border border-red-200 bg-red-50 text-red-700 p-3 text-sm">{error}</div>}

                <button disabled={saving} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white py-3 font-bold">
                  {saving ? "Ekibe bağlanıyor…" : "Kaydı Tamamla ve Ekibe Katıl"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
